import type { AnalyticsRange, StaffAnalyticsOverview } from "@lafayette/schemas";

// What the Overview computes from the answer of the API. Pure, so a test proves each rule. The API sends counts and money; the rates, the
// shares and the drop-offs are written here, once, and every card reads the same numbers.

type Records = StaffAnalyticsOverview["records"];

export const RANGE_OPTIONS: readonly Readonly<{
  value: AnalyticsRange;
  label: string;
  spoken: string;
}>[] = [
  { value: "24h", label: "24 h", spoken: "Last 24 hours" },
  { value: "7d", label: "7 d", spoken: "Last 7 days" },
  { value: "30d", label: "30 d", spoken: "Last 30 days" },
  { value: "90d", label: "90 d", spoken: "Last 90 days" },
  { value: "12m", label: "12 m", spoken: "Last 12 months" },
  { value: "all", label: "All", spoken: "All time" },
];

export const DEFAULT_RANGE: AnalyticsRange = "30d";

/** The words of a range for a sentence: "in the last 30 days". */
export function rangePhrase(range: AnalyticsRange): string {
  return range === "all" ? "in all time" : `in the ${rangeSpoken(range).toLowerCase()}`;
}

export function rangeSpoken(range: AnalyticsRange): string {
  return RANGE_OPTIONS.find((option) => option.value === range)?.spoken ?? range;
}

/** Quotes that became payments, as a share of the quotes of the cohort. `null` when no request was quoted. */
export function quoteToPaidRate(funnel: Records["requestFunnel"]): number | null {
  return funnel.quoted === 0 ? null : Math.min(1, funnel.paid / funnel.quoted);
}

export type LifetimeChange = Readonly<{
  /** Money added in the range: the lifetime value now minus the lifetime value at the start of the range. */
  addedMinor: number;
  /** The change as a share of the value at the start. `null` when the value at the start was zero. */
  share: number | null;
}>;

export function lifetimeChange(kpis: Records["kpis"]): LifetimeChange {
  const addedMinor = kpis.lifetimeValueMinor - kpis.lifetimeValuePreviousMinor;
  return {
    addedMinor,
    share:
      kpis.lifetimeValuePreviousMinor === 0 ? null : addedMinor / kpis.lifetimeValuePreviousMinor,
  };
}

export type FunnelRow = Readonly<{
  key: keyof Records["requestFunnel"];
  label: string;
  count: number;
  /** The share of the requests submitted. */
  share: number;
}>;

const FUNNEL_STAGES = [
  ["submitted", "Submitted"],
  ["quoted", "Quoted"],
  ["paid", "Paid"],
  ["confirmed", "Confirmed"],
  ["completed", "Completed"],
] as const;

export function funnelRows(funnel: Records["requestFunnel"]): readonly FunnelRow[] {
  return FUNNEL_STAGES.map(([key, label]) => ({
    key,
    label,
    count: funnel[key],
    share: funnel.submitted === 0 ? 0 : Math.min(1, funnel[key] / funnel.submitted),
  }));
}

export type FunnelDrop = Readonly<{ from: string; to: string; lost: number }>;

/** The step with the most requests lost between two stages. `null` when no step lost any (a stage can be above the one before it). */
export function biggestDrop(rows: readonly FunnelRow[]): FunnelDrop | null {
  let best: FunnelDrop | null = null;
  for (let index = 1; index < rows.length; index += 1) {
    const before = rows[index - 1];
    const stage = rows[index];
    if (before === undefined || stage === undefined) continue;
    const lost = before.count - stage.count;
    if (lost > 0 && (best === null || lost > best.lost)) {
      best = { from: before.label, to: stage.label, lost };
    }
  }
  return best;
}

export const REQUEST_TYPE_LABELS: Readonly<
  Record<Records["requestsByType"][number]["type"], string>
> = {
  DINING: "Dining",
  VILLA: "Villa",
  EVENT: "Event",
  EXPERIENCE: "Experience",
  SERVICE: "Service",
  TRANSPORT: "Transport",
  OTHER: "Other",
};

export type MessageShare = Readonly<{ label: string; count: number }>;

export function messageRows(messages: Records["messages"]): readonly MessageShare[] {
  return [
    { label: "From the customer", count: messages.customer },
    { label: "From the assistant", count: messages.assistant },
    { label: "From a concierge", count: messages.concierge },
  ];
}

/** The share of the replies to the customer that the assistant wrote. `null` when nobody replied. */
export function assistantShare(messages: Records["messages"]): number | null {
  const replies = messages.assistant + messages.concierge;
  return replies === 0 ? null : messages.assistant / replies;
}

export type ActivityTotals = Readonly<{
  customerMessages: number;
  requests: number;
  payments: number;
  trips: number;
}>;

export function activityTotals(points: Records["activity"]["points"]): ActivityTotals {
  return points.reduce<ActivityTotals>(
    (sum, point) => ({
      customerMessages: sum.customerMessages + point.customerMessages,
      requests: sum.requests + point.requests,
      payments: sum.payments + point.payments,
      trips: sum.trips + point.trips,
    }),
    { customerMessages: 0, requests: 0, payments: 0, trips: 0 },
  );
}

/** True when the customer has no record at all in the range: the page says so in words instead of drawing empty charts. */
export function hasNoRecords(records: Records): boolean {
  const { kpis } = records;
  return (
    kpis.requests +
      kpis.trips +
      kpis.savedItems +
      kpis.conversations +
      kpis.handoffs +
      kpis.paid +
      kpis.notificationsSent +
      records.messages.customer +
      records.messages.assistant +
      records.messages.concierge ===
      0 && kpis.lifetimeValueMinor === 0
  );
}
